import { POST } from "../../API/API";

const DEFAULT_BACKOFF_BASE_MS = 500;
const DEFAULT_BACKOFF_MAX_MS = 10_000;

const createTranscriptionSession = async ({ language }) => {
  return new Promise((resolve, reject) => {
    POST(
      "/api/v1/chat/translate/realtime_session",
      { language },
      (response) => resolve(response?.data),
      (error) => reject(error)
    );
  });
};

export default class RealtimeTranscriptionClient {
  constructor({ onOpen, onClose, onError, onRecognizedText } = {}) {
    this.ws = null;
    this.isConnecting = false;

    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onError = onError;
    this.onRecognizedText = onRecognizedText;

    this.backoffAttempt = 0;
    this.reconnectTimer = null;

    this.language = null;
  }

  get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  async connect({ language } = {}) {
    if (!language) throw new Error("language is required for transcription.");

    this.language = language;

    if (this.isConnecting || this.isConnected) return;
    this.isConnecting = true;

    try {
      const session = await createTranscriptionSession({ language: this.language });

      const accessUrl = session?.access_url;
      if (!accessUrl) throw new Error("Missing access_url from backend");
      await this.connectWebSocket(accessUrl);
    } catch (error) {
      this.isConnecting = false;
      if (this.onError) this.onError(error);
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async reconnect() {
    await this.disconnect();
    return this.connect({ language: this.language });
  }

  scheduleReconnect({ baseMs = DEFAULT_BACKOFF_BASE_MS, maxMs = DEFAULT_BACKOFF_MAX_MS } = {}) {
    if (!this.language) return;
    this.clearReconnectTimer();
    const delay = Math.min(maxMs, baseMs * 2 ** this.backoffAttempt);
    this.backoffAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnect().catch((err) => {
        if (this.onError) this.onError(err);
        this.scheduleReconnect({ baseMs, maxMs });
      });
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  async disconnect() {
    this.clearReconnectTimer();
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
      } catch {}
      this.ws = null;
    }
    this.isConnecting = false;
  }

  async connectWebSocket(accessUrl) {
    await this.disconnect();
    return new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(accessUrl);
        ws.binaryType = "arraybuffer";
        this.ws = ws;

        ws.onopen = () => { if (this.onOpen) this.onOpen(); resolve(); };

        ws.onerror = (event) => {
          const err = event?.error || new Error("Websocket connection error");
          if (this.onError) this.onError(err);
          reject(err);
        };

        ws.onclose = () => { if (this.onClose) this.onClose(); };

        ws.onmessage = (event) => {
          const data = event?.data;
          if (typeof data === "string") this.handleJsonMessage(data);
        };
      } catch (err) { reject(err); }
    });
  }

  handleJsonMessage(raw) {
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    if (parsed?.type === "text_recognized" && this.onRecognizedText) { this.onRecognizedText(parsed); return; }
    if (parsed?.type === "error" && this.onError) { this.onError(new Error(parsed?.message || parsed?.code || "API error")); }
  }

  sendAudio(arrayBuffer) {
    if (!arrayBuffer || arrayBuffer.byteLength === 0 || !this.isConnected) return;
    this.ws.send(arrayBuffer);
  }
}
